import { Edit } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button } from "@mui/material";
import Box from "@mui/material/Box";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import {
	ProjectSaleableService,
	ProjectSaleableServiceGroup,
	useDeleteSaleableServiceMutation,
	useDeleteSaleableServiceGroupMutation,
	useGetAllSaleableServicesLazyQuery,
} from "@repo/server-api";
import { useCurrencyFormatter } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";

import ConformationDialog from "@repo/legacy/components/shared/dialogs/Conformation";
import DialogService from "@repo/legacy/components/shared/dialogs/DialogService";
import DialogServiceGroup from "@repo/legacy/components/shared/dialogs/DialogServiceGroup";

const Services = () => {
	const { t } = useTranslation();
	const formatCurrency = useCurrencyFormatter();

	const [openDialogService, setOpenDialogService] = useState<boolean>(false);
	const [openDialogServiceGroup, setOpenDialogServiceGroup] = useState<boolean>(false);
	const [service, setService] = useState<
		ProjectSaleableService | ProjectSaleableServiceGroup | null
	>(null);
	const [groups, setGroups] = useState<ProjectSaleableServiceGroup[]>([]);
	const [filteredData, setFilteredData] = useState<
		(ProjectSaleableServiceGroup | ProjectSaleableService)[]
	>([]);

	const [deleteService] = useDeleteSaleableServiceMutation({
		onCompleted(data) {
			if (data.deleteSaleableService.boolean) {
				getAllServices();
			}
		},
	});

	const [deleteServiceGroup] = useDeleteSaleableServiceGroupMutation({
		onCompleted(data) {
			if (data.deleteSaleableServiceGroup.boolean) {
				getAllServices();
			}
		},
	});

	const [getAllServices] = useGetAllSaleableServicesLazyQuery({
		fetchPolicy: "network-only",
		onCompleted(data) {
			if (data) {
				const tmp: React.SetStateAction<(ProjectSaleableServiceGroup | ProjectSaleableService)[]> =
					[];
				const tmpGroups: React.SetStateAction<ProjectSaleableServiceGroup[]> = [];
				data.allSaleableServices.forEach((service) => {
					if (service.services?.length != undefined) {
						const tmpServices: React.SetStateAction<ProjectSaleableService[]> = [];
						tmp.push({
							id: service.id,
							originalId: service.id,
							name: service.name,
							budget: {
								amount: 0,
								hasAmountLimit: false,
								hasTimeLimit: false,
								hours: 0,
							},
							sellingPrice: service.sellingPrice ?? 0,
							services: [],
							hasFixSellingPrice: false,
						});
						service.services.forEach((s) => {
							const ss = {
								id: s.id,
								originalId: s.id,
								name: s.name,
								budget: {
									amount: 0,
									hasAmountLimit: false,
									hasTimeLimit: false,
									hours: 0,
								},
								sellingPrice: s.sellingPrice ?? 0,
								unit: {
									id: s.unit?.id ?? "",
									originalId: s.unit?.id ?? "",
									name: s.unit?.name ?? "",
									shortCode: s.unit?.shortCode ?? "",
								},
								hasFixSellingPrice: false,
								purchasePrice: s.purchasePrice ?? 0,
								vatRate: s.vatRate ?? 0,
							};
							tmp.push(ss);
							tmpServices.push(ss);
						});
						tmpGroups.push({
							id: service.id,
							originalId: service.id,
							name: service.name,
							sellingPrice: service.sellingPrice ?? 0,
							services: tmpServices,
							budget: {
								amount: 0,
								hasAmountLimit: false,
								hasTimeLimit: false,
								hours: 0,
							},
							hasFixSellingPrice: false,
						});
					}
				});
				setGroups(tmpGroups);
				setFilteredData(tmp);
			}
		},
	});

	useEffect(() => {
		getAllServices();
	}, [getAllServices]);

	const columns: GridColDef<ProjectSaleableServiceGroup | ProjectSaleableService>[] = useMemo(
		() => [
			{
				field: "name",
				headerName: t("listing"),
				width: 250,
				flex: 1,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.name;
				},
			},
			{
				field: "unit",
				headerName: t("unit"),
				width: 200,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.unit ? params.row.unit.name : "";
				},
			},
			{
				field: "purchasePrice",
				headerName: t("purchase-price"),
				width: 150,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.purchasePrice >= 0
						? (formatCurrency(params.row.purchasePrice) ?? "")
						: "";
				},
			},
			{
				field: "sellingPrice",
				headerName: t("selling-price"),
				width: 150,
				sortable: false,
				renderCell(params: GridCellParams) {
					return formatCurrency(params.row.sellingPrice) ?? "";
				},
			},
			{
				field: "merge",
				headerName: t("margin"),
				width: 150,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.purchasePrice >= 0
						? (formatCurrency(params.row.sellingPrice - params.row.purchasePrice) ?? "")
						: "";
				},
			},
			{
				field: "vatRate",
				headerName: t("vat-rate"),
				width: 150,
				sortable: false,
				renderCell(params: GridCellParams) {
					return params.row.vatRate >= 0
						? parseFloat(params.row.vatRate.toString()).toFixed(3) + " %"
						: "";
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 150,
				renderCell: (params: GridCellParams) => {
					const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);

					const handleYes = (id: string) => {
						if ("unit" in params.row) {
							deleteService({ variables: { id: id } });
						} else {
							deleteServiceGroup({ variables: { id: id } });
						}
						setOpenDeleteConformation(false);
					};
					const openDelete = () => {
						setOpenDeleteConformation(true);
					};

					const openEdit = (service: ProjectSaleableServiceGroup | ProjectSaleableService) => {
						setService(service);
						if ("services" in service) {
							setOpenDialogServiceGroup(true);
						} else {
							setOpenDialogService(true);
						}
					};

					return (
						<div>
							<Button data-testid="edit-button" onClick={() => openEdit(params.row)}>
								<Edit />
							</Button>
							<Button data-testid="delete-button" onClick={() => openDelete()}>
								<DeleteIcon />
							</Button>
							<ConformationDialog
								text={
									params.row?.services
										? t("services.deleteGroupConformation")
										: t("services.deleteConformation")
								}
								setOpenDeleteConformation={setOpenDeleteConformation}
								openPopup={openDeleteConformation}
								handleYes={() => handleYes(params.id.toString())}
							/>
						</div>
					);
				},
			},
		],
		[t, deleteService, deleteServiceGroup, formatCurrency],
	);

	return (
		<div>
			<h3 className="purpleHeader">{t("services")}</h3>
			<Box className="w-100">
				<Button
					className="left blueButton" style={{ marginRight: 20 }}
					color="secondary"
					variant="contained"
					onClick={() => {
						setService({
							id: "",
							name: "",
							purchasePrice: 0,
							sellingPrice: 0,
							vatRate: 0,
							unit: { id: "", originalId: "", name: "", shortCode: "" },
							budget: {
								amount: 0,
								hasAmountLimit: false,
								hasTimeLimit: false,
								hours: 0,
							},
							hasFixSellingPrice: false,
							originalId: "",
						});
						setOpenDialogService(true);
					}}>
					{t("create-new-service")}
				</Button>
				<Button
					className="left blueButton" style={{ marginRight: 20 }}
					color="secondary"
					variant="contained"
					onClick={() => {
						setService({
							id: "",
							name: "",
							sellingPrice: 0,
							budget: {
								amount: 0,
								hasAmountLimit: false,
								hasTimeLimit: false,
								hours: 0,
							},
							hasFixSellingPrice: false,
							originalId: "",
							services: [],
						});
						setOpenDialogServiceGroup(true);
					}}>
					{t("create-new-service-group")}
				</Button>
			</Box>
			<div className="w-100 clear">
				{filteredData && filteredData.length > 0 ? (
					<div>
						<Box sx={{ height: "75vh", width: "100%" }}>
							<DataGridPremium
								getRowId={(row: ProjectSaleableService | ProjectSaleableServiceGroup) => row.id}
								rows={filteredData}
								columns={columns}
								disableRowSelectionOnClick
								className="table"
								hideFooterPagination={true}
								getCellClassName={(
									params: GridCellParams<ProjectSaleableServiceGroup | ProjectSaleableService>,
								) => {
									if (("unit" in params.row && !params.row.unit?.id) || "services" in params.row) {
										return "greyBackground";
									}
									return "";
								}}
							/>
						</Box>
					</div>
				) : (
					t("services.noServices")
				)}
				<DialogService
					groups={groups}
					setGroups={setGroups}
					open={openDialogService}
					setOpen={setOpenDialogService}
					service={service as ProjectSaleableService}
					refreshData={getAllServices}
				/>
				<DialogServiceGroup
					open={openDialogServiceGroup}
					setOpen={setOpenDialogServiceGroup}
					service={service as ProjectSaleableServiceGroup}
					refreshData={getAllServices}
				/>
			</div>
		</div>
	);
};

export default Services;
